import { type JSX, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { isTruthy } from "@vega-core/utils/isTruthy";

import { RouterContext } from "./RouterContext";
import { type RouterContextInterface } from "./types";

export const RouterProvider = ({ children }: { children: JSX.Element }) => {
  const nav = useNavigate();
  const [preventNavigate, setPreventNavigate] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [previousPath, setPreviousPath] = useState<string>();
  const navigate = useCallback(
    (path: string) => {
      if (preventNavigate) {
        setShowConfirmModal(true);
        setPreviousPath(path);
      } else {
        nav(path);
      }
    },
    [nav, preventNavigate],
  );

  const back = useCallback(() => {
    if (preventNavigate) {
      setPreviousPath(undefined);
      setShowConfirmModal(true);
    } else {
      history.back();
    }
  }, [preventNavigate]);

  const handleConfirmNavigate = useCallback(() => {
    setPreventNavigate(false);
    setShowConfirmModal(false);

    if (isTruthy(previousPath)) {
      nav(previousPath);
      setPreviousPath(undefined);
    } else {
      history.back();
    }
  }, [nav, previousPath]);

  const value: RouterContextInterface = useMemo(
    () => ({
      back,
      handleConfirmNavigate,
      navigate,
      preventNavigate,
      setPreventNavigate,
      setShowConfirmModal,
      showConfirmModal,
    }),
    [back, handleConfirmNavigate, navigate, preventNavigate, showConfirmModal],
  );

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
};
